import type { GridApi, IRowNode } from "ag-grid-community";
import React, { useEffect, useRef } from "react";
import { useResizeObserver } from "usehooks-ts";

import type { Level } from "@performyard/service-contracts";

import { DescriptionClamp } from "./DescriptionClamp";
import { LevelAccordionRow } from "./LevelAccordionRow";

import type { SuperRowContext } from "./types";

type SuperRowLayoutProps = {
	node: IRowNode;
	api: GridApi;
	context: SuperRowContext;
	rowId: string;
	description: string;
	levels: Level[];
	isLevelExpected: (rank: number) => boolean;
	// Omitted entirely on surfaces with no "current" concept (e.g. the role interior page).
	isLevelCurrent?: (rank: number) => boolean;
};

// Shared by CompetencySuperRowContent (assignment tables) and RoleCompetencySuperRowContent (role
// interior) — everything about a super row's content except where the description/levels/chip flags
// come from is identical between the two surfaces.
export const SuperRowLayout = ({
	node,
	api,
	context,
	rowId,
	description,
	levels,
	isLevelExpected,
	isLevelCurrent,
}: SuperRowLayoutProps): React.ReactElement => {
	const containerRef = useRef<HTMLDivElement>(null!);
	// Corrects the deterministic estimate from getRowHeight once real text has laid out — mirrors
	// the Surveys super row's own measure-and-correct pattern.
	const { height = 0 } = useResizeObserver<HTMLDivElement>({ ref: containerRef, box: "border-box" });

	useEffect(() => {
		if (height > 0 && node.rowHeight !== height) {
			node.setRowHeight(height);
			api.onRowHeightChanged();
		}
	}, [height, api, node]);

	const sortedLevels = [...levels].sort((a, b) => a.rank - b.rank);
	const lowestRank = sortedLevels[0]?.rank;
	const highestRank = sortedLevels[sortedLevels.length - 1]?.rank;
	const hasMultipleLevels = sortedLevels.length > 1;

	return (
		// px-11 (44px) lines the content up with the competency name text in the grid — the 40px
		// caret column plus the theme's 4px cell padding — so the indent itself separates the
		// accordions from the table rows (PY-14103; the card that used to do this is gone). The
		// right side mirrors it so the rows sit symmetrically.
		<div ref={containerRef} className="bg-neutral-0 px-11 py-4">
			<DescriptionClamp
				content={description}
				isExpanded={context.isDescriptionExpanded(rowId)}
				onToggle={() => context.onToggleDescription(rowId)}
			/>
			<div className="mt-3 max-h-[400px] overflow-y-auto">
				{sortedLevels.map((level, index) => (
					<LevelAccordionRow
						key={level.rank}
						level={level}
						isLowest={hasMultipleLevels && level.rank === lowestRank}
						isHighest={hasMultipleLevels && level.rank === highestRank}
						isCurrent={isLevelCurrent?.(level.rank) ?? false}
						isExpected={isLevelExpected(level.rank)}
						isExpanded={context.isLevelExpanded(rowId, level.rank)}
						isLast={index === sortedLevels.length - 1}
						onToggle={() => context.onToggleLevel(rowId, level.rank)}
					/>
				))}
			</div>
		</div>
	);
};
